import { CubeReducerState } from 'modules/Cube/domain/cube.domain.types'
import { getAcceptQuoteAvailable } from './getAcceptQuoteAvailable'
import { getArchiveQuoteAvailable } from './getArchiveQuoteAvailable'
import { getContractLengthEditingAvailable } from './getContractLengthEditingAvailable'
import { getDeleteLatestQuoteDraftAvailable } from './getDeleteLatestQuoteDraftAvailable'
import { getExecuteQuoteAvailable } from './getExecuteQuoteAvailable'
import { getPreviewQuoteAvailable } from './getPreviewQuoteAvailable'
import { getPublishQuoteAvailable } from './getPublishQuoteAvailable'
import { getEditQuoteAvailable } from './getEditQuoteAvailable'
import { getSetRecipientsAvailable } from './getSetRecipientsAvailable'
import { getDuplicateQuoteAvailable } from './getDuplicateQuoteAvailable'
import { getRevokeQuoteAvailable } from './getRevokeQuoteAvailable'
import { getExternalIntegrationsAvailable } from './getExternalIntegrationsAvailable'
import { getSaveTemplateAvailable } from './getSaveTemplateAvailable'
import { getCreateQuoteFromTemplateAvailable } from './getCreateQuoteFromTemplateAvailable'
import { getEditPaymentTermsAvailable } from './getEditPaymentTermsAvailable'
import { getEditCurrencyAvailable } from './getEditCurrencyAvailable'
import { getEditCountersignersAvailable } from './getEditCountersignersAvailable'
import { getEditAmendmentEffectiveFromAvailable } from './getEditAmendmentEffectiveFromAvailable'
import { getReadyToSignQuoteAvailable } from './getReadyToSignQuoteAvailable'
import compose from 'lodash/fp/compose'
import {
  disableIfSuperseded,
  hideIfSuperseded
} from 'modules/Cube/domain/postActionStages/queries/availableFeatures/utils/quoteSuperseded'
import { disableIfAnotherAmendmentOutForSignature } from 'modules/Cube/domain/postActionStages/queries/availableFeatures/utils/amendmentOutForSignature'

export const getQuoteFeaturesAvailable = (
  prevState: CubeReducerState
): CubeReducerState['queries']['availableFeatures']['quote'] => {
  /**
   * Each feature composes the 'global' rules that hold for it. The supersede is one:
   * an amendment the backend has ruled out can never be executed, so anything that would move it
   * on comes off the screen, and editing stays where it is but stops responding so the sidebar
   * can say why. `edit` is the one the rest of the editor reads, so disabling it reaches the
   * content editor, the sidebar fields and the pricing table at once.
   *
   * As on the schedule side, each feature gets its own compose rather than sharing one pipeline -
   * plenty of global rules still sit inside individual features, and lifting them out to here is
   * what this shape is for.
   */
  const superseded = hideIfSuperseded(prevState)
  const supersededButShown = disableIfSuperseded(prevState)
  /**
   * Only one amendment of a schedule can be out for signature at a time, so while another one is,
   * this one can't be sent for signature or marked as signed. Editing and publishing carry on.
   */
  const anotherOutForSignature =
    disableIfAnotherAmendmentOutForSignature(prevState)

  return {
    contractLengthEditing: getContractLengthEditingAvailable(prevState),
    publish: compose(superseded)(getPublishQuoteAvailable(prevState)),
    accept: compose(
      superseded,
      anotherOutForSignature
    )(getAcceptQuoteAvailable(prevState)),
    readyToSign: compose(
      superseded,
      anotherOutForSignature
    )(getReadyToSignQuoteAvailable()),
    execute: compose(superseded)(getExecuteQuoteAvailable(prevState)),
    preview: getPreviewQuoteAvailable(prevState),
    /** Deliberately untouched: archiving is what is left to do with a superseded quote. */
    archive: getArchiveQuoteAvailable(prevState),
    deleteLatestDraft: compose(superseded)(
      getDeleteLatestQuoteDraftAvailable(prevState)
    ),
    edit: compose(supersededButShown)(getEditQuoteAvailable(prevState)),
    setRecipients: getSetRecipientsAvailable(prevState),
    duplicate: compose(superseded)(getDuplicateQuoteAvailable(prevState)),
    revoke: compose(superseded)(getRevokeQuoteAvailable(prevState)),
    integrations: getExternalIntegrationsAvailable(prevState),
    saveTemplate: getSaveTemplateAvailable(prevState),
    createQuoteFromTemplate: getCreateQuoteFromTemplateAvailable(prevState),
    editPaymentTerms: getEditPaymentTermsAvailable(prevState),
    editCurrency: getEditCurrencyAvailable(prevState),
    editCountersigners: getEditCountersignersAvailable(prevState),
    /** Reads `edit` itself, so it composes the rule rather than inheriting it. */
    editAmendmentEffectiveFrom: compose(supersededButShown)(
      getEditAmendmentEffectiveFromAvailable(prevState)
    )
  }
}
